// /opt/roadoptix/frontend/src/pages/BrokerCreateCarrier.js
import React, { useMemo, useState } from "react";
import { useAuth } from "../components/AuthContext";
import { apiGetBroker, apiPostBroker } from "../api";
import { useNavigate } from "react-router-dom";
// 
// import { apiGetBroker, apiPostBroker } from "../api";
// const getAdminEmail = () => localStorage.getItem("adminEmail") || "brokeradmin@roadoptix.com";
// 
// async function apiGet(path) {
//   const resp = await fetch(`${API_BASE}${path}`, {
//     headers: {
//       "x-admin-email": getAdminEmail(),
//       Accept: "application/json",
//     },
//   });
// 
//   const text = await resp.text();
//   let json = null;
//   try {
//     json = text ? JSON.parse(text) : null;
//   } catch (e) {
//     throw new Error(`Non-JSON response (${resp.status}): ${text.slice(0, 160)}`);
//   }
// 
//   if (!resp.ok) {
//     const msg = json?.error || `Request failed (${resp.status})`;
//     const details = json?.details ? `: ${json.details}` : "";
//     throw new Error(`${msg}${details}`);
//   }
// 
//   return json;
// }
// 
// async function apiPost(path, body) {
//   const resp = await fetch(`${API_BASE}${path}`, {
//     method: "POST",
//     headers: {
//       "x-admin-email": getAdminEmail(),
//       "Content-Type": "application/json",
//       Accept: "application/json",
//     },
//     body: JSON.stringify(body),
//   });
// 
//   const text = await resp.text();
//   let json = null;
//   try {
//     json = text ? JSON.parse(text) : null;
//   } catch (e) {
//     throw new Error(`Non-JSON response (${resp.status}): ${text.slice(0, 160)}`);
//   }
// 
//   if (!resp.ok) {
//     const msg = json?.error || `Request failed (${resp.status})`;
//     const details = json?.details ? `: ${json.details}` : "";
//     throw new Error(`${msg}${details}`);
//   }
// 
//   return json;
// }
// 
function moneyMaskPhone(v) {
  return v;
}

function upper(v) {
  return v === null || v === undefined ? "" : String(v).toUpperCase();
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtMoneyMillions(v) {
  // FMCSA returns "1000" for $1,000,000 (thousands).
  // We'll display "$1,000,000" if v=1000; otherwise fallback.
  const n = num(v);
  if (n === null) return "";
  const dollars = n * 1000; // common FMCSA convention in this payload
  try {
    return dollars.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  } catch {
    return `$${dollars}`;
  }
}

function badgeStyles(kind) {
  // kind: good | warn | bad | info
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid #ddd",
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  if (kind === "good") return { ...base, color: "#0a6", borderColor: "#bfe9d0", background: "#effbf4" };
  if (kind === "warn") return { ...base, color: "#a66a00", borderColor: "#ffe2b3", background: "#fff8e8" };
  if (kind === "bad") return { ...base, color: "#c21f1f", borderColor: "#f3b6b6", background: "#fff0f0" };
  return { ...base, color: "#555", borderColor: "#ddd", background: "#f7f7f7" };
}

function Badge({ kind, label }) {
  return <span style={badgeStyles(kind)}>{label}</span>;
}

/**
 * Build a human-readable compliance summary from the FMCSA preview fields you already return.
 * IMPORTANT: cargoInsuranceRequired="U" is NOT a failure (unknown / not required in this dataset).
 */
function buildFmcsaSummary(preview) {
  if (!preview) return null;

  const allowedToOperate = upper(preview.allowedToOperate);
  const statusCode = upper(preview.statusCode);
  const common = upper(preview.commonAuthorityStatus);
  const contract = upper(preview.contractAuthorityStatus);

  // "DOT License" (carrier exists + can operate)
  const dotOk =
    allowedToOperate === "Y" ||
    statusCode === "A" ||
    common === "A" ||
    contract === "A";

  const dot = {
    label: "DOT License",
    kind: dotOk ? "good" : "bad",
    value: dotOk ? "Valid (Active)" : "Not Active / Not Authorized",
    details: `allowedToOperate=${allowedToOperate || "null"}, statusCode=${statusCode || "null"}`,
  };

  // Operating Authority
  const authOk = common === "A" || contract === "A";
  const auth = {
    label: "Operating Authority",
    kind: authOk ? "good" : dotOk ? "warn" : "bad",
    value: authOk ? "Active (For-Hire)" : "Not Active / Unknown",
    details: `common=${common || "null"}, contract=${contract || "null"}`,
  };

  // Liability (BIPD)
  const bipdReq = upper(preview.bipdInsuranceRequired); // Y/N/?
  const bipdOn = num(preview.bipdInsuranceOnFile);
  const bipdNeed = num(preview.bipdRequiredAmount);

  let bipdKind = "info";
  let bipdValue = "Not provided";
  let bipdDetails = "";

  if (bipdReq === "Y") {
    const ok = (bipdOn ?? 0) >= (bipdNeed ?? 0) && (bipdNeed ?? 0) > 0;
    bipdKind = ok ? "good" : "bad";
    bipdValue = ok
      ? `Valid (${fmtMoneyMillions(bipdOn)} on file)`
      : `Insufficient (${fmtMoneyMillions(bipdOn)} on file; needs ${fmtMoneyMillions(bipdNeed)})`;
    bipdDetails = `required=${bipdReq}, onFile=${bipdOn}, requiredAmount=${bipdNeed}`;
  } else if (bipdReq === "N") {
    bipdKind = "info";
    bipdValue = "Not required";
    bipdDetails = `required=${bipdReq}`;
  } else if (bipdReq) {
    // Unknown/other code
    bipdKind = "info";
    bipdValue = `Requirement: ${bipdReq}`;
    bipdDetails = `required=${bipdReq}, onFile=${bipdOn ?? "null"}, requiredAmount=${bipdNeed ?? "null"}`;
  }

  const bipd = {
    label: "Liability Insurance (BIPD)",
    kind: bipdKind,
    value: bipdValue,
    details: bipdDetails,
  };

  // Cargo insurance
  const cargoReq = upper(preview.cargoInsuranceRequired); // often "U" in your payload
  const cargoOn = num(preview.cargoInsuranceOnFile);

  let cargoKind = "info";
  let cargoValue = "Not provided";
  let cargoDetails = `required=${cargoReq || "null"}, onFile=${cargoOn ?? "null"}`;

  if (cargoReq === "Y") {
    const ok = (cargoOn ?? 0) > 0;
    cargoKind = ok ? "good" : "bad";
    cargoValue = ok ? "Valid" : "Missing / zero on file";
  } else if (cargoReq === "N") {
    cargoKind = "info";
    cargoValue = "Not required";
  } else if (cargoReq === "U" || cargoReq === "") {
    cargoKind = "info";
    cargoValue = "Not required by FMCSA (U/Unknown)";
  } else {
    cargoKind = "info";
    cargoValue = `Requirement: ${cargoReq}`;
  }

  const cargo = {
    label: "Cargo Insurance",
    kind: cargoKind,
    value: cargoValue,
    details: cargoDetails,
  };

  // MCS-150
  const mcs150 = upper(preview.mcs150Outdated);
  const mcs = {
    label: "MCS-150",
    kind: mcs150 === "Y" ? "warn" : "good",
    value: mcs150 === "Y" ? "Outdated" : "Current",
    details: `mcs150Outdated=${mcs150 || "null"}`,
  };

  // Safety rating (null is common; not a fail)
  const sr = upper(preview.safetyRating);
  let srKind = "info";
  let srValue = "Not Rated";
  if (sr) {
    if (sr.includes("UNSAT")) {
      srKind = "bad";
      srValue = "Unsatisfactory";
    } else if (sr.includes("CONDITIONAL")) {
      srKind = "warn";
      srValue = "Conditional";
    } else if (sr.includes("SATIS")) {
      srKind = "good";
      srValue = "Satisfactory";
    } else {
      srKind = "info";
      srValue = sr;
    }
  }
  const safety = {
    label: "Safety Rating",
    kind: srKind,
    value: srValue,
    details: `safetyRating=${sr || "null"}`,
  };

  const items = [dot, auth, bipd, cargo, mcs, safety];

  const anyBad = items.some((i) => i.kind === "bad");
  const anyWarn = items.some((i) => i.kind === "warn");
  const overallKind = anyBad ? "bad" : anyWarn ? "warn" : "good";
  const overallLabel =
    overallKind === "good"
      ? "FMCSA Compliant – Eligible to Operate"
      : overallKind === "warn"
      ? "FMCSA Caution – Review Recommended"
      : "FMCSA Not Compliant – Action Required";

  return {
    overallKind,
    overallLabel,
    items,
    fetchedAt: preview.fetchedAt || null,
  };
}

export default function BrokerCreateCarrier() {
  const { adminEmail } = useAuth();
  const navigate = useNavigate();

  const [dot, setDot] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState("");

  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // (UI-only) suggestion flag: backend job should actually schedule / refresh
  const [autoRecheck, setAutoRecheck] = useState(true);

  // form state
  const [form, setForm] = useState({
    dotNumber: "",
    companyName: "",
    companyPhone: "",
    companyEmail: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postal: "",
    country: "US",
    userEmail: "",
    userName: "",
    overwriteCompanyFields: false,
  });

  const canLookup = useMemo(() => String(dot || "").trim().length >= 3, [dot]);

  function patchForm(p) {
    setForm((f) => ({ ...f, ...p }));
  }

  function applyPreviewToForm(preview) {
    const name = preview.legalName || preview.dbaName || "";
    const addr = preview.address || {};

    patchForm({
      dotNumber: String(preview.dotNumber || "").trim(),
      companyName: form.companyName?.trim() ? form.companyName : name,
      address1: form.address1?.trim() ? form.address1 : (addr.address1 || ""),
      city: form.city?.trim() ? form.city : (addr.city || ""),
      state: form.state?.trim() ? form.state : (addr.state || ""),
      postal: form.postal?.trim() ? form.postal : (addr.postal || ""),
      country: form.country?.trim() ? form.country : (addr.country || "US"),
    });
  }

  async function handleLookup() {
    setLookupError("");
    setLookupResult(null);

    const d = String(dot || "").trim();
    if (!d) return;

    setLookupBusy(true);
    try {
      const res = await apiGetBroker(`/broker/carriers/dot/${encodeURIComponent(d)}/preview`, { adminEmail });
      setLookupResult(res);

      patchForm({ dotNumber: d });

      if (res?.preview) {
        applyPreviewToForm(res.preview);
      }
    } catch (e) {
      setLookupError(e.message || String(e));
    } finally {
      setLookupBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError("");

    const payload = {
      dotNumber: String(form.dotNumber || "").trim(),
      userEmail: String(form.userEmail || "").trim().toLowerCase(),
      userName: form.userName?.trim() ? String(form.userName).trim() : undefined,
      companyName: form.companyName?.trim() ? String(form.companyName).trim() : undefined,
      companyPhone: form.companyPhone?.trim() ? moneyMaskPhone(String(form.companyPhone).trim()) : undefined,
      companyEmail: form.companyEmail?.trim() ? String(form.companyEmail).trim().toLowerCase() : undefined,
      address1: form.address1?.trim() ? String(form.address1).trim() : undefined,
      address2: form.address2?.trim() ? String(form.address2).trim() : undefined,
      city: form.city?.trim() ? String(form.city).trim() : undefined,
      state: form.state?.trim() ? String(form.state).trim() : undefined,
      postal: form.postal?.trim() ? String(form.postal).trim() : undefined,
      country: form.country?.trim() ? String(form.country).trim() : "US",
      overwriteCompanyFields: form.overwriteCompanyFields === true,

      // NEW: ask backend to persist the FMCSA snapshot if available
      // (backend must be updated to store this)
      fmcsaSnapshot: lookupResult?.preview?.raw || null,
      fmcsaCheckedAt: lookupResult?.preview?.fetchedAt || null,
      enableAutoComplianceRecheck: autoRecheck === true,
    };

    if (!payload.dotNumber) return setSubmitError("DOT number is required");
    if (!payload.userEmail) return setSubmitError("User email is required");

    setSubmitBusy(true);
    try {
      const created = await apiPostBroker("/broker/carriers/onboard", payload, { adminEmail });
      alert(`Carrier onboarded: ${created?.company?.name || "OK"}`);
      navigate("/broker/customers"); // change to your carriers page route if you have one
    } catch (e2) {
      setSubmitError(e2.message || String(e2));
    } finally {
      setSubmitBusy(false);
    }
  }

  const mode = lookupResult?.mode || "";
  const warning = lookupResult?.warning || "";
  const reasons = lookupResult?.reasons || [];
  const warnings = lookupResult?.warnings || [];

  const fmcsaSummary = useMemo(() => buildFmcsaSummary(lookupResult?.preview), [lookupResult?.preview]);

  return (
    <div style={{ padding: 24, maxWidth: 920 }}>
      <h2 style={{ marginTop: 0 }}>Create Carrier</h2>

      {/* Upgrade suggestions panel (UX-visible) */}
      <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, border: "1px dashed #ccc", background: "#fbfbfb" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Compliance Upgrades (Recommended)</div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "#333" }}>
          <li><b>Store FMCSA snapshot</b> on creation for audit + faster views.</li>
          <li><b>Re-check compliance every 30 days</b> in the background (and on bid/award).</li>
          <li><b>Color-coded badges</b> across broker & carrier UI (board, detail, tender/award screens).</li>
        </ul>
      </div>

      <div style={{ border: "1px solid #ddd", padding: 16, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>1) DOT Lookup</div>

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={dot}
            onChange={(e) => setDot(e.target.value)}
            placeholder="Enter DOT number"
            style={{ padding: 10, minWidth: 240 }}
          />
          <button
            type="button"
            onClick={handleLookup}
            disabled={!canLookup || lookupBusy}
            style={{ padding: "10px 14px" }}
          >
            {lookupBusy ? "Looking up..." : "Lookup"}
          </button>

          <button
            type="button"
            onClick={() => {
              if (lookupResult?.preview) applyPreviewToForm(lookupResult.preview);
            }}
            disabled={!lookupResult?.preview}
            style={{ padding: "10px 14px" }}
            title="Re-apply preview values into the form"
          >
            Apply Preview
          </button>
        </div>

        {lookupError ? (
          <div style={{ marginTop: 10, color: "crimson" }}>
            <b>Lookup error:</b> {lookupError}
          </div>
        ) : null}

        {lookupResult ? (
          <div style={{ marginTop: 12 }}>
            {/* Topline result */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div>
                <b>Result:</b>{" "}
                <span style={{ fontWeight: 900, color: lookupResult.ok ? "green" : "crimson" }}>
                  {lookupResult.ok ? "OK" : "NOT OK"}
                </span>{" "}
                {mode ? <span style={{ color: "#666" }}>({mode})</span> : null}
              </div>

              {fmcsaSummary ? (
                <Badge
                  kind={fmcsaSummary.overallKind}
                  label={fmcsaSummary.overallLabel}
                />
              ) : null}

              {fmcsaSummary?.fetchedAt ? (
                <span style={{ color: "#666", fontSize: 12 }}>
                  Checked: {new Date(fmcsaSummary.fetchedAt).toLocaleString()}
                </span>
              ) : null}
            </div>

            {/* Backend warnings / reasons (from API) */}
            {warning ? (
              <div style={{ marginTop: 6, color: "#8a6d3b" }}>
                <b>Note:</b> {warning}
              </div>
            ) : null}

            {reasons?.length ? (
              <div style={{ marginTop: 6, color: "crimson" }}>
                <b>Reasons:</b>
                <ul style={{ marginTop: 6 }}>
                  {reasons.map((r, idx) => (
                    <li key={idx}>{r}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {warnings?.length ? (
              <div style={{ marginTop: 6, color: "#8a6d3b" }}>
                <b>Warnings:</b>
                <ul style={{ marginTop: 6 }}>
                  {warnings.map((w, idx) => (
                    <li key={idx}>{w}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* NEW: Human readable compliance summary + color-coded badges */}
            {fmcsaSummary ? (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: "1px solid #eee", background: "#fff" }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>FMCSA Compliance Summary</div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  {fmcsaSummary.items.map((it) => (
                    <Badge key={it.label} kind={it.kind} label={`${it.label}: ${it.value}`} />
                  ))}
                </div>

                <div style={{ fontSize: 13, color: "#444" }}>
                  <div><b>FMCSA Name:</b> {lookupResult?.preview?.legalName || lookupResult?.preview?.dbaName || "(none)"}</div>
                  <div>
                    <b>FMCSA Address:</b>{" "}
                    {[
                      lookupResult?.preview?.address?.address1,
                      lookupResult?.preview?.address?.city,
                      lookupResult?.preview?.address?.state,
                      lookupResult?.preview?.address?.postal,
                    ]
                      .filter(Boolean)
                      .join(", ") || "(none)"}
                  </div>

                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: "pointer" }}>Show raw FMCSA fields (debug)</summary>
                    <div style={{ marginTop: 8 }}>
                      <pre style={{ margin: 0, padding: 10, background: "#f7f7f7", borderRadius: 6, overflowX: "auto" }}>
                        {JSON.stringify(lookupResult?.preview, null, 2)}
                      </pre>
                    </div>
                  </details>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <form onSubmit={handleSubmit} style={{ border: "1px solid #ddd", padding: 16, borderRadius: 8 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>2) Carrier Details</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            DOT Number *
            <input
              value={form.dotNumber}
              onChange={(e) => patchForm({ dotNumber: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Company Name
            <input
              value={form.companyName}
              onChange={(e) => patchForm({ companyName: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Company Phone
            <input
              value={form.companyPhone}
              onChange={(e) => patchForm({ companyPhone: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Company Email
            <input
              value={form.companyEmail}
              onChange={(e) => patchForm({ companyEmail: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Address 1
            <input
              value={form.address1}
              onChange={(e) => patchForm({ address1: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Address 2
            <input
              value={form.address2}
              onChange={(e) => patchForm({ address2: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            City
            <input
              value={form.city}
              onChange={(e) => patchForm({ city: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            State
            <input
              value={form.state}
              onChange={(e) => patchForm({ state: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Postal
            <input
              value={form.postal}
              onChange={(e) => patchForm({ postal: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Country
            <input
              value={form.country}
              onChange={(e) => patchForm({ country: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Dispatcher/User Email *
            <input
              value={form.userEmail}
              onChange={(e) => patchForm({ userEmail: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>

          <label>
            Dispatcher/User Name
            <input
              value={form.userName}
              onChange={(e) => patchForm({ userName: e.target.value })}
              style={{ width: "100%", padding: 10 }}
            />
          </label>
        </div>

        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={form.overwriteCompanyFields}
              onChange={(e) => patchForm({ overwriteCompanyFields: e.target.checked })}
            />
            Overwrite existing company fields (if DOT already exists)
          </label>

          {/* UI preference; backend must implement scheduling */}
          <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={autoRecheck}
              onChange={(e) => setAutoRecheck(e.target.checked)}
            />
            Enable background compliance re-check every 30 days (recommended)
          </label>
        </div>

        {submitError ? (
          <div style={{ marginTop: 12, color: "crimson" }}>
            <b>Error:</b> {submitError}
          </div>
        ) : null}

        <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
          <button type="submit" disabled={submitBusy} style={{ padding: "10px 14px" }}>
            {submitBusy ? "Creating..." : "Create Carrier"}
          </button>

          <button type="button" onClick={() => navigate(-1)} style={{ padding: "10px 14px" }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
